import React, { useState, forwardRef, useRef, useCallback } from 'react';
import { Card, Form, Row, Col, Button, Container } from 'react-bootstrap';

const IDCard = forwardRef(({ student, schoolInfo }, ref) => {
  const [cardSize, setCardSize] = useState({
    width: '8.56',
    height: '5.398'
  });

  const [cardColor, setCardColor] = useState('#1e40af');
  const [orientation, setOrientation] = useState('portrait');
  const [textColor, setTextColor] = useState('#ffffff');
  
  const printRef = useRef();

  // Public folder image paths
  const imageUrls = {
    nielitLogo: '/images/nielit.jpeg',
    schoolLogo: '/images/gsk.jpg',
    studentPhoto: student?.photoURL || '/images/student-placeholder.png'
  };

  const handlePrint = useCallback(() => {
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    const width = parseFloat(cardSize.width);
    const height = parseFloat(cardSize.height);

    // Get base URL for images in print window
    const baseUrl = window.location.origin;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>ID Card - ${student?.studentName}</title>
          <meta charset="utf-8">
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
              line-height: 1.2;
            }
            
            body {
              margin: 0;
              padding: 20px;
              background: #f0f0f0;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
              font-family: Arial, sans-serif;
            }
            
            @media print {
              body {
                margin: 0;
                padding: 0;
                background: white;
              }
              
              .print-container {
                box-shadow: none !important;
                margin: 0 !important;
              }
            }
            
            .print-container {
              width: ${width}cm;
              height: ${height}cm;
              background: white;
              box-shadow: 0 4px 8px rgba(0,0,0,0.1);
              position: relative;
            }
            
            .id-card-print {
              width: 100%;
              height: 100%;
              padding: 0.15cm;
            }
            
            /* ID Card Styles */
            .id-card-header {
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 3px 5px;
              min-height: 35px;
            }
            
            .logo-section {
              display: flex;
              align-items: center;
              flex: 1;
            }
            
            .logo-img {
              width: 25px;
              height: 25px;
              object-fit: contain;
              display: block;
            }
            
            .school-info {
              text-align: center;
              flex: 2;
            }
            
            .student-photo {
              width: 50px;
              height: 50px;
              border-radius: 2px;
              overflow: hidden;
              border: 1px solid #ccc;
            }
            
            .student-photo img {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }
            
            .student-details {
              font-size: 0.35rem;
            }
            
            .student-details table {
              width: 100%;
              border-collapse: collapse;
            }
            
            .student-details td {
              padding: 0.5px;
              vertical-align: top;
            }
            
            .validity-badge {
              font-size: 0.25rem;
              padding: 2px;
              text-align: center;
              border-radius: 1px;
              margin-top: 2px;
              width: 100%;
            }
            
            .signature-section {
              font-size: 0.2rem;
            }
            
            .card-footer {
              font-size: 0.25rem;
              padding: 2px;
              text-align: center;
            }

            /* Fallback styles */
            .logo-fallback {
              width: 25px;
              height: 25px;
              background: white;
              border-radius: 2px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 6px;
              font-weight: bold;
              border: 1px solid #ccc;
            }
            
            .photo-fallback {
              width: 50px;
              height: 50px;
              background: #e5e7eb;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 8px;
              color: #6b7280;
              text-align: center;
              border-radius: 2px;
            }

            /* Ensure images load in print */
            img {
              max-width: 100%;
              height: auto;
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <div class="id-card-print">
              ${printContent}
            </div>
          </div>
          <script>
            // Convert relative image paths to absolute URLs for print
            document.addEventListener('DOMContentLoaded', function() {
              const images = document.querySelectorAll('img');
              const baseUrl = '${baseUrl}';
              
              images.forEach(img => {
                if (img.src.startsWith('data:')) return;
                
                if (img.src.includes('/images/')) {
                  // If it's a relative path, make it absolute
                  if (!img.src.startsWith('http')) {
                    img.src = baseUrl + img.getAttribute('src');
                  }
                }
              });
              
              // Wait for images to load before printing
              Promise.all(Array.from(images).filter(img => !img.complete).map(img => 
                new Promise(resolve => { img.onload = img.onerror = resolve; })
              )).then(() => {
                setTimeout(() => {
                  window.print();
                  setTimeout(() => {
                    window.close();
                  }, 1000);
                }, 500);
              });
            });
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  }, [cardSize, student]);

  const getTextColor = (bgColor) => {
    const color = bgColor.replace('#', '');
    const r = parseInt(color.substr(0, 2), 16);
    const g = parseInt(color.substr(2, 2), 16);
    const b = parseInt(color.substr(4, 2), 16);
    const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return brightness > 128 ? '#000000' : '#ffffff';
  };

  const getCardDimensions = () => {
    if (orientation === 'portrait') {
      return {
        width: `${cardSize.width}cm`,
        height: `${cardSize.height}cm`
      };
    } else {
      return {
        width: `${cardSize.height}cm`,
        height: `${cardSize.width}cm`
      };
    }
  };

  const ImageWithFallback = ({ src, alt, fallbackText, className, style, isLogo = false }) => {
    const [hasError, setHasError] = useState(false);

    const handleError = (e) => {
      console.log(`Image failed to load: ${src}`);
      setHasError(true);
    };

    const handleLoad = (e) => {
      console.log(`Image loaded successfully: ${src}`);
    };

    if (hasError) {
      return (
        <div 
          className={isLogo ? "logo-fallback" : "photo-fallback"}
          style={style}
        >
          {fallbackText}
        </div>
      );
    }

    return (
      <img
        src={src}
        alt={alt}
        className={className}
        style={style}
        onError={handleError}
        onLoad={handleLoad}
      />
    );
  };

  const SizeControls = () => (
    <div className="mb-3">
      <Card>
        <Card.Header className="bg-primary text-white">
          <h6 className="mb-0">ID Card Settings</h6>
        </Card.Header>
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={2}>
              <Form.Group>
                <Form.Label>Width (cm)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.1"
                  min="5"
                  max="15"
                  value={cardSize.width}
                  onChange={(e) => setCardSize({ ...cardSize, width: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Height (cm)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.1"
                  min="5"
                  max="15"
                  value={cardSize.height}
                  onChange={(e) => setCardSize({ ...cardSize, height: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Orientation</Form.Label>
                <Form.Select
                  value={orientation}
                  onChange={(e) => setOrientation(e.target.value)}
                >
                  <option value="portrait">Portrait</option>
                  <option value="landscape">Landscape</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Card Color</Form.Label>
                <Form.Control
                  type="color"
                  value={cardColor}
                  onChange={(e) => {
                    setCardColor(e.target.value);
                    setTextColor(getTextColor(e.target.value));
                  }}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Button variant="success" onClick={handlePrint} className="w-100">
                🖨️ Print ID Card
              </Button>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col>
              <small className="text-muted">
                Standard ID Card Size: 8.56cm × 5.398cm (CR80)
              </small>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );

  const cardDimensions = getCardDimensions();

  return (
    <Container fluid>
      <SizeControls />
      
      <div className="d-flex justify-content-center mb-4">
        <div 
          ref={printRef}
          style={{
            width: cardDimensions.width,
            height: cardDimensions.height,
            padding: '0.15cm',
            backgroundColor: '#fff',
            border: '1px solid #ddd',
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          {/* ID Card Content */}
          <div style={{ 
            height: '100%',
            borderRadius: '3px', 
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'white',
            border: `1px solid ${cardColor}`
          }}>
            {/* Header */}
            <div 
              style={{ 
                backgroundColor: cardColor, 
                color: textColor,
                borderBottom: `1px solid ${textColor}`,
              }} 
              className="id-card-header"
            >
              {/* Left Logo - NIELIT */}
              <div className="logo-section">
                <ImageWithFallback
                  src={imageUrls.nielitLogo}
                  alt="NIELIT Logo"
                  fallbackText="NIELIT"
                  className="logo-img"
                  style={{ 
                    marginRight: '3px',
                    backgroundColor: 'white',
                    padding: '1px',
                    borderRadius: '2px'
                  }}
                  isLogo={true}
                />
                <div style={{ fontSize: '0.3rem', marginLeft: '3px' }}>
                  <div style={{ fontWeight: 'bold' }}>NIELIT</div>
                  <div>Govt. of India</div>
                </div>
              </div>
              
              {/* School Info */}
              <div className="school-info">
                <div style={{ fontWeight: 'bold', fontSize: '0.4rem' }}>
                  {schoolInfo.name}
                </div>
                <div style={{ fontSize: '0.25rem' }}>
                  {schoolInfo.address}
                </div>
              </div>

              {/* Right Logo - School */}
              <div className="logo-section" style={{ justifyContent: 'flex-end' }}>
                <ImageWithFallback
                  src={imageUrls.schoolLogo}
                  alt="School Logo"
                  fallbackText="SCHOOL"
                  className="logo-img"
                  style={{ 
                    marginLeft: '3px',
                    backgroundColor: 'white',
                    padding: '1px',
                    borderRadius: '2px'
                  }}
                  isLogo={true}
                />
              </div>
            </div>

            {/* Body */}
            <div style={{ 
              flex: 1,
              padding: '4px',
              display: 'flex',
              backgroundColor: 'white'
            }}>
              {/* Left - Photo */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                marginRight: '5px',
                width: '55px'
              }}>
                <div 
                  className="student-photo"
                  style={{ 
                    border: `1px solid ${cardColor}`,
                    backgroundColor: '#f8f9fa'
                  }}
                >
                  <ImageWithFallback
                    src={imageUrls.studentPhoto}
                    alt="Student"
                    fallbackText="STUDENT PHOTO"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
                <div 
                  className="validity-badge"
                  style={{ 
                    backgroundColor: cardColor,
                    color: textColor,
                  }}
                >
                  <div style={{ fontWeight: 'bold' }}>VALID</div>
                  <div>{new Date().getFullYear()}-{new Date().getFullYear() + 1}</div>
                </div>
              </div>
              
              {/* Right - Details */}
              <div className="student-details" style={{ flex: 1 }}>
                <table>
                  <tbody>
                    <tr>
                      <td style={{ width: '35%', fontWeight: 'bold' }}>Name:</td>
                      <td>{student?.studentName}</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 'bold' }}>Roll No:</td>
                      <td>{student?.rollNumber}</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 'bold' }}>Class:</td>
                      <td>{student?.grade}-{student?.section}</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 'bold' }}>Contact:</td>
                      <td>{student?.phone}</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 'bold' }}>Father:</td>
                      <td>{student?.fatherName}</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 'bold' }}>Blood Group:</td>
                      <td>{student?.bloodGroup}</td>
                    </tr>
                  </tbody>
                </table>

                {/* Signature Section */}
                <div 
                  className="signature-section"
                  style={{ 
                    marginTop: '4px',
                    paddingTop: '2px',
                    borderTop: '0.5px solid #ccc',
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}
                >
                  <div>
                    <div>Issued: {new Date().toLocaleDateString()}</div>
                    <div style={{ borderTop: '0.5px solid #ccc', marginTop: '1px', paddingTop: '1px', width: '35px' }}>
                      Student Signature
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ borderBottom: '0.5px solid #ccc', paddingBottom: '1px', marginBottom: '1px', width: '40px' }}>
                      Authorized Signatory
                    </div>
                    <div>School Stamp</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div 
              className="card-footer"
              style={{ 
                backgroundColor: '#f8f9fa',
                borderTop: `1px solid ${cardColor}`,
              }}
            >
              <div style={{ fontWeight: 'bold', color: cardColor }}>
                {schoolInfo.phone} | {schoolInfo.email} | {schoolInfo.website}
              </div>
              <div style={{ color: '#6c757d' }}>
                This card is non-transferable • Loss should be reported immediately
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="text-center mt-3">
        <Card>
          <Card.Body>
            <small className="text-muted">
              <strong>Preview:</strong> {cardDimensions.width} × {cardDimensions.height} • 
              Orientation: {orientation === 'portrait' ? 'Portrait' : 'Landscape'}
            </small>
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
});

IDCard.defaultProps = {
  student: {
    studentName: 'Rahul Sharma',
    rollNumber: '2024123',
    grade: '12',
    section: 'B',
    phone: '+91 9876543210',
    fatherName: 'Shri Sanjay Sharma',
    bloodGroup: 'B+',
    photoURL: '/images/student-placeholder.png'
  },
  schoolInfo: {
    name: 'DELHI PUBLIC SCHOOL',
    address: 'Education Nagar, New Delhi - 110096',
    phone: '+91 11 2654 7890',
    email: 'info@dpschool.edu',
    website: 'www.dpschool.edu',
  }
};

export default IDCard;